import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Eye, Copy, Check } from 'lucide-react';
import { openLink } from '@tma.js/sdk-react';

import { useTournamentIdQuery } from '@/services/endpoints/tournaments';
import { useTournamentMatchesQuery } from '@/services/endpoints/matches';
import { useGetIapUserQuery } from '@/services/customizationApi';
import { Loader } from '@/components/common/Loader';
import { cdnImage } from '@/lib/cdnImage';
import { cn } from '@/lib/cn';
import { EMatchStatus } from '@/types/enums';
import type { Participant, TournamentMatches } from '@/types/tournament';

/**
 * Watch Party screen — mirrors mobile-app/app/tournamentID/watch-party.tsx.
 *
 * Shows all currently-live matches (status COLLECTING) for an auto-handled
 * tournament. Each match exposes two eye buttons (one per participant) that
 * deeplink into Clash Royale via the URL returned in
 * `match.metadata[participant.id]`. The deeplink is opened through Telegram's
 * `openLink` so the WebView itself stays put.
 */
export function WatchParty() {
  const { id = '' } = useParams<{ id: string }>();
  const numericId = Number(id);
  const validId = id && !Number.isNaN(numericId);

  const tournamentQuery = useTournamentIdQuery(numericId, { skip: !validId });
  const matchesQuery = useTournamentMatchesQuery(
    { id: numericId },
    { skip: !validId, pollingInterval: 10000 },
  );

  const liveMatches = useMemo<TournamentMatches[]>(() => {
    const list = matchesQuery.data?.data ?? [];
    return list.filter((m) => m.match_status === EMatchStatus.COLLECTING);
  }, [matchesQuery.data]);

  const tournament = tournamentQuery.data;

  if (tournamentQuery.isLoading || matchesQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="text-sm text-danger">Tournament not found</p>
        <Link to="/" className="mt-3 inline-block text-sm text-brand">
          ← Back
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header className="flex items-center gap-3">
        {tournament.image && (
          <img
            src={cdnImage(tournament.image)}
            alt=""
            className="h-14 w-14 shrink-0 rounded-lg object-cover"
          />
        )}
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wide text-danger">Watch party</p>
          <h1 className="truncate text-base font-bold text-fg">{tournament.title}</h1>
        </div>
      </header>

      {liveMatches.length === 0 ? (
        <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
          No live matches right now. They appear here as soon as battles start.
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {liveMatches.map((match) => (
            <MatchRow key={match.id} match={match} />
          ))}
        </ul>
      )}
    </div>
  );
}

function MatchRow({ match }: { match: TournamentMatches }) {
  const p1 = match.first_participant;
  const p2 = match.second_participant;
  const canWatch1 = !!match.metadata && !!p1 && !!match.metadata[String(p1.id)];
  const canWatch2 = !!match.metadata && !!p2 && !!match.metadata[String(p2.id)];

  return (
    <li
      className={cn(
        'rounded-xl border border-[#ED304C]/60 p-3',
        'shadow-[inset_0_0_30px_0_rgba(237,48,76,0.18)]',
      )}
    >
      <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-wide text-fg-muted">
        <span>Round {match.round}</span>
        <span className="rounded-full bg-danger/15 px-2 py-0.5 font-bold text-danger">
          Live
        </span>
      </div>
      <div className="flex flex-col gap-2">
        <ParticipantWatchRow participant={p1} canWatch={canWatch1} url={canWatch1 && p1 ? match.metadata![String(p1.id)] : null} />
        <div className="text-center text-[10px] font-bold uppercase text-fg-muted">vs</div>
        <ParticipantWatchRow participant={p2} canWatch={canWatch2} url={canWatch2 && p2 ? match.metadata![String(p2.id)] : null} />
      </div>
    </li>
  );
}

function ParticipantWatchRow({
  participant,
  canWatch,
  url,
}: {
  participant: Participant | null;
  canWatch: boolean;
  url: string | null;
}) {
  const [copied, setCopied] = useState(false);
  // Per-participant IAP fetch. RTK Query deduplicates by userId, so when the
  // same player appears across multiple matches/rounds we only hit the
  // backend once. `skip` short-circuits when the row is for a missing or
  // un-profiled participant (e.g., "TBD" slots in early rounds).
  const userId = (participant?.profile?.user as { id?: number | string } | undefined)?.id;
  const { data: iapUser } = useGetIapUserQuery(userId ?? '', {
    skip: userId == null,
  });
  const bannerUri = iapUser?.banner?.media_uri ?? null;
  // The Laravel serializer (ProfileInfoResource) literally returns the string
  // "unknown" when the in-game nickname is null — skip it and fall through
  // to the QLASH user record. ShortUserResource exposes `nickname` (not
  // `username`, which our typings claim).
  const infoNick = participant?.profile?.info?.nickname;
  const userObj = participant?.profile?.user as
    | { nickname?: string; name?: string; username?: string }
    | undefined;
  const nickname =
    (infoNick && infoNick !== 'unknown' ? infoNick : null) ??
    userObj?.nickname ??
    userObj?.username ??
    userObj?.name ??
    'TBD';
  const onWatch = (): void => {
    if (!canWatch || !url) return;
    // Telegram Mini Apps don't let us navigate to a custom URI scheme
    // (clashroyale://...) from inside the WebView. The workaround is the
    // HTTPS-redirect pattern: open a static page on our origin (which IS
    // https, so Telegram's `openLink` accepts it) in the system browser;
    // the page then does `location.replace(clashroyale://...)`, which the
    // browser hands off to the OS app handler.
    const redirect =
      `${window.location.origin}/watch-redirect.html?to=${encodeURIComponent(url)}`;
    console.info('[watch-party] opening via redirect:', redirect);
    openLink.ifAvailable(redirect);
  };
  const onCopy = (): void => {
    if (!url) return;
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="relative flex items-center justify-between gap-3 overflow-hidden rounded-lg bg-bg-secondary p-3">
      {bannerUri && (
        <>
          <img
            src={cdnImage(bannerUri)}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-r from-bg/90 via-bg/60 to-bg/40"
          />
        </>
      )}
      <span className="relative truncate text-sm font-semibold text-fg">{nickname}</span>
      <div className="relative flex items-center gap-2">
        {canWatch && (
          <button
            type="button"
            onClick={onCopy}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-bg-tertiary text-fg-muted transition-opacity active:opacity-80"
            aria-label="Copy deeplink"
          >
            {copied ? <Check aria-hidden className="h-4 w-4 text-success" /> : <Copy aria-hidden className="h-4 w-4" />}
          </button>
        )}
        <button
          type="button"
          onClick={onWatch}
          disabled={!canWatch}
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-full transition-opacity active:opacity-80',
            canWatch ? 'bg-danger text-white' : 'bg-bg-tertiary text-fg-muted opacity-50',
          )}
          aria-label={canWatch ? `Watch ${nickname}` : 'No live deeplink'}
        >
          <Eye aria-hidden className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
